import classnames from 'classnames';
import React, {useContext, useRef, useState} from 'react';
import {withRouter, WithRouterProps} from 'react-router';

import {Footer} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';

import {useShowTranslationToggle} from '../QuestionTypes/shared/TranslationSection';
import EmptyQuestionShell from './EmptyQuestionShell/EmptyQuestionShell';
import {MobilePracticeViewContext} from './MobilePracticeViewProvider.context';
import {useOptionEliminatorReset} from './OptionEliminatorStore/OptionEliminatorStore';
import './practice-view.scss';
import {PracticeViewWrapper} from './PracticeViewWrapper/PracticeViewWrapper';
import QuestionRenderer from './QuestionRenderer/QuestionRenderer.react';
import QuestionsList from './QuestionsList/QuestionsList.react';
import {QuestionListProps, QuestionProps} from './QuestionsList/questionsList.types';
import {useIsFeatureFlagEnabled} from '../FeatureFlag/featureFlag.utils';

interface PracticeViewProps extends WithRouterProps {
  removeQuestionListOnMobile?: boolean; // needed for the time being to conditionally remove questionlist since its not needed on mobile view TODO remove after moving question list into tool bar for AssignmentPracticeView
  activeQuestion: any; // PropTypes.instanceOf(getModelForResourceType('question_v3')), TODO-KBC: Build typescript methods
  activeQuestionSet: any; // PropTypes.instanceOf(getModelForResourceType('question_set_v1')), TODO-KBC: Build typescript methods
  activeQuestionStore: any; // PropTypes.instanceOf(QuestionTypeStore), TODO-KBC: Build typescript methods
  activeGuess: any; // PropTypes.instanceOf(getModelForResourceType('guess_v1')), TODO-KBC: Build typescript methods
  activeSubject: any; // PropTypes.instanceOf(getModelForResourceType('subject_v2')), TODO-KBC: Build typescript methods
  questionProps?: QuestionProps;
  questionRendererProps: {
    actions: React.ReactNode;
    rendererType: 'PreGuess' | 'PostGuess';
    interactive?: boolean;
    navigateBackProps: {
      disabled: boolean;
      handleNavigation?: () => void;
    } | null;
    navigateNextProps: {
      disabled: boolean;
      handleNavigation?: () => void;
    } | null;
    headingActions: React.ReactNode;
    modifyTitle?: () => {};
  };
  questionSetProps?: Object;
  questionSets: any; // ImmutablePropTypes.listOf(PropTypes.instanceOf(getModelForResourceType('question_set_v1'))), TODO-KBC: Build typescript methods
  questionsListProps?: QuestionListProps;
  stageComponent?: React.ReactNode;
  showDifficulty?: boolean;
  titleBar?: React.ReactElement;
  isLoading: boolean;
  toolbar?: React.ReactNode;
  activeQuestionDisplayOrientation?: 'default' | 'vertical' | 'horizontal';
  hasQuestionSetAccessPredicate?: (questionSet: any) => boolean;
  QuestionListComponent?: React.ReactElement;
  partialCreditEnabled?: boolean;
}

export const hasQuestionSetAccessPredicateDefaultValue = (questionSet) => {
  const subject = questionSet.getSubject();
  const isRestricted = subject.getMeta().hasRestrictedFreePractice();
  if (sessionStore.isStudent() && isRestricted) {
    return false;
  }
  return (
    (subject.getMeta().isUserHasAccess() as boolean) || (questionSet.getMeta().isFree() as boolean)
  );
};

const MAIN_WRAPPER_PADDING = 32;

const PracticeView = ({
  removeQuestionListOnMobile = false,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeGuess,
  activeSubject,
  activeQuestionDisplayOrientation = 'default',
  questionProps = {
    onQuestionClick: () => {}
  },
  // @ts-ignore - yelling b/c object doesn't have correct props initially
  questionRendererProps = {},
  questionSetProps = {},
  questionsListProps,
  questionSets,
  stageComponent,
  showDifficulty = true,
  titleBar,
  isLoading,
  params,
  toolbar,
  hasQuestionSetAccessPredicate = hasQuestionSetAccessPredicateDefaultValue,
  QuestionListComponent,
  partialCreditEnabled = false
}: PracticeViewProps) => {
  useOptionEliminatorReset();

  const {studentId} = params;

  const showToggle = useShowTranslationToggle();
  // Container refs for handling scrolling on question updates
  const mainWrapperRef = useRef<HTMLDivElement>(null);
  const answerInputPaneRef = useRef<HTMLDivElement>(null);
  const stackedContainerRef = useRef<HTMLDivElement>(null);
  const postSubmissionScrollPointRef = useRef<HTMLDivElement>(null);
  const {isMobile, isQuestionsListInView, setIsQuestionsListInView} =
    useContext(MobilePracticeViewContext);

  const {isFeatureEnabled: isDennisQuestionPreviewUpdatedView} = useIsFeatureFlagEnabled(
    'dennis_question_preview_match_practice_view'
  );

  // controlling the position of the question resizer to persist the position when switching between questions
  const [activeQuestionResizerPosition, setActiveQuestionResizerPosition] = useState<number>(() => {
    // When there is no logged in user, everything is done via SSR (for SEO purposes).
    // This means that when there is no logged in user, attempting to access `window` will cause a crash.
    // We can safely default to an arbitrary value since this will not be rendered in the browser anyway.
    if (!sessionStore.hasValidSession()) {
      return 0;
    }

    return isMobile
      ? 150
      : ((mainWrapperRef.current?.getBoundingClientRect().width ?? window.innerWidth) -
          MAIN_WRAPPER_PADDING) /
          2;
  });

  const makeMobileOnQuestionClick = (args) => {
    return () => {
      if (isMobile && isQuestionsListInView) {
        setIsQuestionsListInView(false);
      }
      questionProps.onQuestionClick(...args)();
    };
  };

  let QuestionListToRender: JSX.Element | null = null;
  if (QuestionListComponent) {
    QuestionListToRender = QuestionListComponent;
  } else if (questionsListProps) {
    QuestionListToRender = (
      <QuestionsList
        {...questionsListProps!}
        className={classnames('practice-view__questions-list flex flex-col', {
          'practice-view__questions-list--in-view': isQuestionsListInView
        })}
        activeQuestion={activeQuestion}
        questionSetProps={{
          ...questionSetProps,
          hasQuestionSetAccessPredicate
        }}
        questionProps={{
          ...questionProps,
          showDifficulty,
          onQuestionClick: (...args) => makeMobileOnQuestionClick(args)
        }}
        questionSets={questionSets}
        isLoading={isLoading}
      />
    );
  }

  return (
    <PracticeViewWrapper
      activeQuestion={activeQuestion}
      questionRendererType={questionRendererProps?.rendererType}
      studentId={studentId}
      mainWrapperRef={mainWrapperRef}
      displayOrientation={activeQuestionDisplayOrientation}
      answerInputPaneRef={answerInputPaneRef}
      stackedContainerRef={stackedContainerRef}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
    >
      <div className='practice-view__title-bar'>
        {titleBar &&
          React.cloneElement(
            titleBar,
            isMobile && !isQuestionsListInView && !stageComponent
              ? {
                  imgHref: null,
                  title: 'Questions',
                  backButton: (
                    <TitleBar.BackButton
                      as='button'
                      label='Questions'
                      icon='chevron-left'
                      onClick={() => setIsQuestionsListInView(true)}
                    />
                  )
                }
              : {}
          )}
        {toolbar}
      </div>
      <div className='practice-view__main-wrapper flex grow justify-center overflow-hidden background-subtle'>
        {stageComponent ? (
          <div className='practice-view__stage-component-scroll-handler u-bgc_slate-100 u-width_100pc absolute overflow-auto'>
            <div className='practice-view__stage-component-wrapper u-mar-lr_auto'>
              {stageComponent}
              <Footer minimal />
            </div>
          </div>
        ) : (
          <div className='practice-view__main flex grow overflow-hidden'>
            {(!isMobile || !removeQuestionListOnMobile) && QuestionListToRender}
            {/* Main Question Area */}
            <div
              className={classnames('practice-view__question-area grow overflow-auto', {
                'practice-view__question-area--translation-enabled': showToggle
              })}
              ref={mainWrapperRef}
              data-testid='practice-view__question-area'
            >
              <div
                className={classnames('practice-view__question-area u-height_100pc grow', {
                  'practice-view__question-area--dim': isMobile && isQuestionsListInView,
                  'u-mar-b_5': isMobile && removeQuestionListOnMobile, // needed since new sticky question list footer on AssignmentPracticeView cuts footer disclaimer off
                  'practice-view__mobile-redesign': activeQuestionDisplayOrientation !== 'default'
                })}
              >
                {activeQuestion ? (
                  <QuestionRenderer
                    {...questionRendererProps}
                    activeQuestion={activeQuestion}
                    activeQuestionSet={activeQuestionSet}
                    activeQuestionStore={activeQuestionStore}
                    activeGuess={activeGuess}
                    activeSubject={activeSubject}
                    answerInputPaneRef={answerInputPaneRef}
                    displayOrientation={activeQuestionDisplayOrientation}
                    isMobile={isMobile}
                    onResize={(position) => setActiveQuestionResizerPosition(position)}
                    showDifficulty={showDifficulty}
                    resizerPosition={activeQuestionResizerPosition}
                    stackedContainerRef={stackedContainerRef}
                    postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                    hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
                    partialCreditEnabled={partialCreditEnabled}
                    isDennisQuestionPreviewUpdatedView={isDennisQuestionPreviewUpdatedView}
                  />
                ) : (
                  <EmptyQuestionShell />
                )}
                {activeQuestionDisplayOrientation === 'default' && <Footer minimal />}
              </div>
            </div>
          </div>
        )}
      </div>
    </PracticeViewWrapper>
  );
};

export default withRouter(PracticeView);
